import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { Box, Paper } from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import PageContainer from "../../common_components/PageContainer";
import Pagination from "../../common_components/Pagination";
import ConfirmActionModal from "../../common_components/ConfirmActionModal";
import NotesModal from "../../common_components/NotesModal";
import { toast } from "../../common_components/Toast/ToastUtils";
import { OutreachFilterBar, OutreachTabBar } from "./Component/OutreachTrackerHeader";
import OutreachTrackerMap from "./Component/OutreachTrackerMap";
import OutreachTrackerDrawer from "./Drawer/OutreachTrackerDrawer";
import { selectPermissions, selectUser } from "../../redux/authSlice";
import { fetchOutreachFilters, fetchOutreachList, fetchOutreachListAll, manageOutreachTracker, selectOutreachRows, selectOutreachPagination, selectOutreachLoading, selectOutreachFilters, selectOutreachFiltersLoaded, selectOutreachSaving, } from "../../redux/outreachTrackerSlice";
import { OUTREACH_PAGINATION } from "./utils";
import { monthParam, getStatusLabel, getTypeLabel } from "./Component/shared";
import exportOutreachTracker from "./exportOutreachTracker";
import { useLayout } from "../../contexts/LayoutContext";
import "../../styles/OutreachTracker.css";
const applySearchAndSort = (rows, searchText, sortConfig) => {
    let result = [...rows];
    const search = searchText.trim().toLowerCase();
    if (search) {
        result = result.filter((r) => [r.client?.name, (r.am || []).map((a) => a.name).join(" "), r.os?.name, r.tracker_name, getStatusLabel(r.status), getTypeLabel(r.type)]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()
            .includes(search));
    }
    if (sortConfig?.columnId && sortConfig.direction) {
        const { columnId, direction } = sortConfig;
        const getVal = (r) => {
            if (columnId === "client")
                return r.client?.name || "";
            if (columnId === "am")
                return (r.am || []).map((a) => a.name).join(", ");
            if (columnId === "os")
                return r.os?.name || "";
            if (columnId === "tracker")
                return r.tracker_name || "";
            if (columnId === "type")
                return r.type || "";
            if (columnId === "status")
                return r.status || "";
            if (columnId === "launch_date")
                return r.launch_date || "";
            if (columnId === "end_date")
                return r.end_date || "";
            return r[columnId] ?? "";
        };
        result.sort((a, b) => {
            const av = getVal(a);
            const bv = getVal(b);
            if (typeof av === "string")
                return direction === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
            return direction === "asc" ? av - bv : bv - av;
        });
    }
    return result;
};
const OutreachTrackerMain = () => {
    const dispatch = useDispatch();
    const permissions = useSelector(selectPermissions);
    const canManage = permissions?.outreach_trackers?.actions?.includes("Manage Trackers") ?? false;
    // client_scope: 'assigned' → show only trackers where the logged-in user is the AM or OS;
    // 'all' (or unset) → show everything. The user's id matches the am_id/os_id stored on a row.
    const user = useSelector(selectUser);
    const currentUserId = String(user?.id || user?._id || "");
    const restrictToAssigned = permissions?.outreach_trackers?.client_scope === "assigned";
    const rows = useSelector(selectOutreachRows);
    const pagination = useSelector(selectOutreachPagination);
    const loading = useSelector(selectOutreachLoading);
    const filters = useSelector(selectOutreachFilters);
    const filtersLoaded = useSelector(selectOutreachFiltersLoaded);
    const saving = useSelector(selectOutreachSaving);
    const [activeTab, setActiveTab] = useState("active");
    const [searchText, setSearchText] = useState("");
    // Debounced copy of the search text — drives the server query so we don't fire a
    // request per keystroke. 'assigned' scope keeps searching client-side (it already
    // fetches the whole tab and paginates locally), so it doesn't use this.
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [accountManagerId, setAccountManagerId] = useState("");
    const [clientId, setClientId] = useState("");
    const [status, setStatus] = useState("");
    const [type, setType] = useState("");
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [page, setPage] = useState(OUTREACH_PAGINATION.DEFAULT_PAGE);
    const [sortConfig, setSortConfig] = useState({ columnId: null, direction: "asc" });
    const [hiddenColumns, setHiddenColumns] = useState(new Set());
    const [columnOrder, setColumnOrder] = useState(null);
    const [columnVisibilityAnchor, setColumnVisibilityAnchor] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerMode, setDrawerMode] = useState("add");
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [notesState, setNotesState] = useState({ open: false, record: null });
    const [deleteState, setDeleteState] = useState({
        open: false,
        loading: false,
        record: null,
    });
    const { setIsSidebarExpanded } = useLayout();
    useEffect(() => {
        setIsSidebarExpanded(false);
    }, [setIsSidebarExpanded]);
    useEffect(() => {
        if (!filtersLoaded)
            dispatch(fetchOutreachFilters());
    }, [dispatch, filtersLoaded]);
    // Debounce the search input before it hits the server.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchText.trim()), 350);
        return () => clearTimeout(t);
    }, [searchText]);
    // A new search starts from page 1 (skip the initial empty-string run).
    const didMountSearch = useRef(false);
    useEffect(() => {
        if (!didMountSearch.current) {
            didMountSearch.current = true;
            return;
        }
        setPage(OUTREACH_PAGINATION.DEFAULT_PAGE);
    }, [debouncedSearch]);
    const fetchList = useCallback(() => {
        dispatch(fetchOutreachList({
            tab: activeTab,
            account_manager_id: accountManagerId || undefined,
            client_id: clientId || undefined,
            status: status || undefined,
            type: type || undefined,
            month: monthParam(selectedMonth) || undefined,
            // 'assigned' scope filters + paginates client-side, so fetch the whole tab once
            // and let the client-side search apply; 'all' scope searches on the server.
            search: restrictToAssigned ? undefined : (debouncedSearch || undefined),
            page: restrictToAssigned ? 1 : page,
            limit: restrictToAssigned ? 100 : OUTREACH_PAGINATION.LIMIT,
        }));
    }, [dispatch, activeTab, accountManagerId, clientId, status, type, selectedMonth, debouncedSearch, page, restrictToAssigned]);
    useEffect(() => {
        fetchList();
    }, [fetchList]);
    // 'assigned' scope: keep only rows where the user is the AM or OS (no other users' data).
    const scopedRows = useMemo(() => {
        if (!restrictToAssigned)
            return rows;
        if (!currentUserId)
            return [];
        return rows.filter((r) => (r.am || []).some((a) => String(a.id) === currentUserId) || String(r.os?.id || "") === currentUserId);
    }, [rows, restrictToAssigned, currentUserId]);
    // 'all' scope searches on the server (it drives the paginated total), so client-side
    // search is skipped there to avoid hiding a server-matched row and desyncing the count.
    // 'assigned' scope still searches client-side. Sorting always applies to the current rows.
    const processedRows = useMemo(() => applySearchAndSort(scopedRows, restrictToAssigned ? searchText : "", sortConfig), [scopedRows, restrictToAssigned, searchText, sortConfig]);
    // Server paginates when scope is 'all'; for 'assigned' the full tab is fetched, so we
    // paginate the filtered rows client-side and report the filtered totals.
    const effectiveTotal = restrictToAssigned ? processedRows.length : pagination.total;
    const effectiveTotalPages = restrictToAssigned
        ? Math.max(1, Math.ceil(processedRows.length / OUTREACH_PAGINATION.LIMIT))
        : pagination.total_pages;
    const displayedRows = restrictToAssigned
        ? processedRows.slice((page - 1) * OUTREACH_PAGINATION.LIMIT, page * OUTREACH_PAGINATION.LIMIT)
        : processedRows;
    // Keep the page in range after client-side filtering (e.g. fewer assigned rows than the page index).
    useEffect(() => {
        if (restrictToAssigned && page > effectiveTotalPages)
            setPage(effectiveTotalPages);
    }, [restrictToAssigned, page, effectiveTotalPages]);
    const toFirstPage = useCallback(() => setPage(OUTREACH_PAGINATION.DEFAULT_PAGE), []);
    const handleTabChange = useCallback((t) => { setActiveTab(t); toFirstPage(); }, [toFirstPage]);
    const handleAccountManagerChange = useCallback((v) => { setAccountManagerId(v); toFirstPage(); }, [toFirstPage]);
    const handleClientChange = useCallback((v) => { setClientId(v); toFirstPage(); }, [toFirstPage]);
    const handleStatusChange = useCallback((v) => { setStatus(v); toFirstPage(); }, [toFirstPage]);
    const handleTypeChange = useCallback((v) => { setType(v); toFirstPage(); }, [toFirstPage]);
    const handleMonthChange = useCallback((val) => {
        setSelectedMonth(val === "ALL_TIME" ? null : val);
        toFirstPage();
    }, [toFirstPage]);
    const handleAddClick = useCallback(() => {
        setDrawerMode("add");
        setSelectedRecord(null);
        setDrawerOpen(true);
    }, []);
    const handleEdit = useCallback((row) => {
        setDrawerMode("edit");
        setSelectedRecord(row);
        setDrawerOpen(true);
    }, []);
    const handleNotes = useCallback((row) => {
        setNotesState({ open: true, record: row });
    }, []);
    const handleSaveNotes = useCallback(async (notes) => {
        if (!notesState.record)
            return;
        await dispatch(manageOutreachTracker({ action: "update_notes", id: notesState.record.id, notes })).unwrap();
        toast.success("Notes updated.");
        fetchList();
    }, [dispatch, notesState.record, fetchList]);
    const handleDelete = useCallback((row) => {
        setDeleteState({ open: true, loading: false, record: row });
    }, []);
    const handleConfirmDelete = useCallback(async () => {
        if (!deleteState.record)
            return;
        try {
            setDeleteState((p) => ({ ...p, loading: true }));
            await dispatch(manageOutreachTracker({ action: "delete", id: deleteState.record.id })).unwrap();
            setDeleteState({ open: false, loading: false, record: null });
            const newTotal = Math.max(0, pagination.total - 1);
            const lastPage = Math.max(1, Math.ceil(newTotal / OUTREACH_PAGINATION.LIMIT));
            if (page > lastPage)
                setPage(lastPage);
            else
                fetchList();
        }
        catch (error) {
            toast.error(typeof error === "string" ? error : "Failed to delete.");
            setDeleteState((p) => ({ ...p, loading: false }));
        }
    }, [dispatch, deleteState.record, pagination.total, page, fetchList]);
    // Export the WHOLE filtered set for the current tab (all pages), not just the page on
    // screen. Fetches every matching row, then mirrors the table's client-side scope/search.
    const handleExport = useCallback(async () => {
        try {
            const allRows = await dispatch(fetchOutreachListAll({
                tab: activeTab,
                account_manager_id: accountManagerId || undefined,
                client_id: clientId || undefined,
                status: status || undefined,
                type: type || undefined,
                month: monthParam(selectedMonth) || undefined,
                // 'assigned' scope searches client-side (below); 'all' scope on the server.
                search: restrictToAssigned ? undefined : (debouncedSearch || undefined),
            })).unwrap();
            const scoped = restrictToAssigned
                ? (currentUserId
                    ? allRows.filter((r) => (r.am || []).some((a) => String(a.id) === currentUserId) || String(r.os?.id || "") === currentUserId)
                    : [])
                : allRows;
            const rowsToExport = applySearchAndSort(scoped, restrictToAssigned ? searchText : "", sortConfig);
            if (!rowsToExport.length) {
                toast.error("No records to export.");
                return;
            }
            // File name: OutreachTracker_<Tab>_<YYYY_MM_DD_HH_mm_ss>
            const pad = (n) => String(n).padStart(2, "0");
            const d = new Date();
            const stamp = `${d.getFullYear()}_${pad(d.getMonth() + 1)}_${pad(d.getDate())}_${pad(d.getHours())}_${pad(d.getMinutes())}_${pad(d.getSeconds())}`;
            const tabLabel = activeTab.charAt(0).toUpperCase() + activeTab.slice(1);
            exportOutreachTracker(rowsToExport, hiddenColumns, `OutreachTracker_${tabLabel}_${stamp}`);
        }
        catch (error) {
            toast.error(typeof error === "string" ? error : "Failed to export.");
        }
    }, [dispatch, activeTab, accountManagerId, clientId, status, type, selectedMonth, restrictToAssigned, debouncedSearch, currentUserId, searchText, sortConfig, hiddenColumns]);
    const handleSortChange = useCallback((columnId, direction) => setSortConfig({ columnId: columnId, direction }), []);
    return (_jsxs(PageContainer, { children: [_jsxs(Box, { className: "outreach-tracker-container", children: [_jsx(OutreachFilterBar, { accountManagerId: accountManagerId, setAccountManagerId: handleAccountManagerChange, clientId: clientId, setClientId: handleClientChange, status: status, setStatus: handleStatusChange, type: type, setType: handleTypeChange, selectedMonth: selectedMonth, onMonthChange: handleMonthChange, filters: filters }), _jsxs(Paper, { className: "outreach-tracker-card", elevation: 0, children: [_jsx(OutreachTabBar, { activeTab: activeTab, onTabChange: handleTabChange, searchText: searchText, setSearchText: setSearchText, onColumnVisibilityClick: (e) => setColumnVisibilityAnchor(e.currentTarget), onAddClick: handleAddClick, onExport: handleExport, canManage: canManage }), _jsx(Box, { className: "outreach-tracker-table-wrapper", children: _jsx(OutreachTrackerMap, { rows: displayedRows, loading: loading, isFirstLoad: loading && rows.length === 0, searchText: searchText, onEdit: handleEdit, onDelete: handleDelete, onNotes: handleNotes, sortConfig: sortConfig, onSortChange: handleSortChange, hiddenColumns: hiddenColumns, onColumnVisibilityChange: setHiddenColumns, columnVisibilityAnchor: columnVisibilityAnchor, onColumnVisibilityClose: () => setColumnVisibilityAnchor(null), columnOrder: columnOrder, onColumnOrderChange: setColumnOrder, canManage: canManage }) }), effectiveTotal > OUTREACH_PAGINATION.LIMIT && (_jsx(Box, { className: "outreach-tracker-pagination-wrapper", children: _jsx(Pagination, { currentPage: page, totalPages: effectiveTotalPages, onPageChange: setPage, maxVisiblePages: 5, totalEntries: effectiveTotal, itemsPerPage: OUTREACH_PAGINATION.LIMIT, showEntriesMessage: true }) }))] })] }), _jsx(OutreachTrackerDrawer, { open: drawerOpen, onClose: () => setDrawerOpen(false), mode: drawerMode, record: selectedRecord, filters: filters, onSaved: fetchList }), notesState.open && (_jsx(NotesModal, { open: notesState.open, onClose: () => setNotesState({ open: false, record: null }), initialNotes: notesState.record?.notes || "", onSave: handleSaveNotes, actionLoading: saving, className: "outreach-notes-paper" })), deleteState.open && (_jsx(ConfirmActionModal, { className: "delete-popup-container", open: deleteState.open, loading: deleteState.loading, title: "Are You Sure?", description: "You're about to delete this outreach tracker. This action cannot be undone.", confirmText: "Yes, Proceed", cancelText: "No, Cancel", onClose: () => !deleteState.loading && setDeleteState({ open: false, loading: false, record: null }), onConfirm: handleConfirmDelete }))] }));
};
export default OutreachTrackerMain;
